import React, { useEffect, useRef, useState, useCallback } from 'react';
import { motion, useMotionValue, useSpring } from 'framer-motion';

const CustomCursor = () => {
  const [isHovering, setIsHovering] = useState(false);
  const [isPointer, setIsPointer] = useState(false);
  const [cursorText, setCursorText] = useState('');
  const [cursorSize, setCursorSize] = useState('normal');
  const cursorRef = useRef(null);
  
  const cursorX = useMotionValue(-100);
  const cursorY = useMotionValue(-100);
  
  // Optimized spring config for smoother performance
  const springConfig = { damping: 30, stiffness: 400, mass: 0.3 };
  const cursorXSpring = useSpring(cursorX, springConfig);
  const cursorYSpring = useSpring(cursorY, springConfig);

  useEffect(() => {
    // Check if touch device
    const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
    if (isTouchDevice) return;

    // Throttle mouse move for better performance
    let rafId = null;
    const moveCursor = (e) => {
      if (rafId) return;
      rafId = requestAnimationFrame(() => {
        cursorX.set(e.clientX);
        cursorY.set(e.clientY);
        rafId = null;
      });
    };

    const handleMouseOver = (e) => {
      const target = e.target;
      const isInteractive = 
        target.tagName === 'BUTTON' || 
        target.tagName === 'A' || 
        target.closest('button') || 
        target.closest('a') ||
        target.classList.contains('cursor-pointer') ||
        window.getComputedStyle(target).cursor === 'pointer';
      
      // Check for special cursor states
      const cursorType = target.getAttribute('data-cursor');
      if (cursorType === 'view') {
        setCursorText('VIEW');
        setCursorSize('large');
      } else if (cursorType === 'drag') {
        setCursorText('DRAG');
        setCursorSize('large');
      } else if (cursorType === 'explore') {
        setCursorText('EXPLORE');
        setCursorSize('large');
      } else {
        setCursorText('');
        setCursorSize('normal');
      }
      
      setIsPointer(isInteractive);
      setIsHovering(isInteractive);
    };

    const handleMouseLeave = () => {
      cursorX.set(-100);
      cursorY.set(-100);
    };

    window.addEventListener('mousemove', moveCursor, { passive: true });
    window.addEventListener('mouseover', handleMouseOver, { passive: true });
    document.addEventListener('mouseleave', handleMouseLeave);

    return () => {
      window.removeEventListener('mousemove', moveCursor);
      window.removeEventListener('mouseover', handleMouseOver);
      document.removeEventListener('mouseleave', handleMouseLeave);
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, [cursorX, cursorY]);

  const getScale = () => {
    if (cursorSize === 'large') return 2.5;
    if (isHovering) return 1.5;
    return 1;
  };

  return (
    <>
      {/* Main cursor */}
      <motion.div
        ref={cursorRef}
        className="custom-cursor will-change-transform"
        style={{
          x: cursorXSpring,
          y: cursorYSpring,
        }}
        animate={{
          scale: getScale(),
          opacity: isHovering || cursorText ? 0.6 : 0.4,
        }}
        transition={{ duration: 0.2, ease: "easeOut" }}
      >
        {cursorText && (
          <span className="absolute inset-0 flex items-center justify-center text-white text-xs font-bold tracking-wider">
            {cursorText}
          </span>
        )}
      </motion.div>
      
      {/* Inner cursor dot */}
      <motion.div
        className="custom-cursor-dot will-change-transform"
        style={{
          x: cursorXSpring,
          y: cursorYSpring,
        }}
        animate={{
          scale: isPointer || cursorText ? 0 : 1,
        }}
        transition={{ duration: 0.2, ease: "easeOut" }}
      />

      {/* Trailing effect */}
      <motion.div
        className="custom-cursor-trail will-change-transform"
        style={{
          x: cursorXSpring,
          y: cursorYSpring,
        }}
        animate={{
          scale: isHovering ? 2 : 1.5,
          opacity: isHovering ? 0.1 : 0.05,
        }}
        transition={{ duration: 0.4, ease: "easeOut" }}
      />
    </>
  );
};

export default CustomCursor;